import { PiPlus } from 'react-icons/pi';
import { Card } from 'src/modules/shared/components/atoms/cards';
import { CardBody } from 'src/modules/shared/components/atoms/cards/card-body';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { ReportWidgetCatalogItem } from 'src/api/generated';
import {
  type DashboardWidgetKind,
  toDashboardWidgetKind,
} from './reports-dashboard.utils';
import { WidgetThumbnail } from './widget-thumbnail';

/**
 * Widget catalog shown before configuring a new dashboard card.
 */
export const WidgetCatalogPanel: React.FC<{
  items: ReportWidgetCatalogItem[];
  onSelect: (kind: DashboardWidgetKind) => void;
  inModal?: boolean;
}> = ({ items, onSelect, inModal = false }) => {
  const { t } = useI18n();

  return (
    <div
      className={
        inModal
          ? 'space-y-3 bg-transparent p-4 sm:p-5'
          : 'space-y-3 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm'
      }
    >
      {!inModal && (
        <div>
          <Text
            as="h3"
            size="h6"
            weight="semibold"
            className="text-primary-900"
          >
            {t('reports.dashboard.catalog.title')}
          </Text>
          <Text size="small" className="text-neutral-600">
            {t('reports.dashboard.catalog.description')}
          </Text>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {items.map((item) => (
          <Card
            key={item.kind}
            appearance="soft"
            variant="secondary"
            className="overflow-hidden border border-neutral-200"
          >
            <CardBody className="space-y-3">
              <WidgetThumbnail kind={item.kind} />
              <div className="space-y-1">
                <Text
                  as="h4"
                  size="small"
                  weight="semibold"
                  className="text-primary-900"
                >
                  {item.title}
                </Text>
                <Text size="small" className="text-neutral-600">
                  {item.description}
                </Text>
              </div>
              <Button
                size="sm"
                appearance="soft"
                variant="secondary"
                onClick={() => onSelect(toDashboardWidgetKind(item.kind))}
              >
                <PiPlus className="h-4 w-4" />
                {t('reports.dashboard.actions.configure')}
              </Button>
            </CardBody>
          </Card>
        ))}
      </div>
    </div>
  );
};
